import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, MessageSquare, CalendarDays, FileText, Settings as SettingsIcon, Home, Palette } from "lucide-react";
import { useAuth } from "../auth";
import { api } from "../api";
import { LOGO } from "../data";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import Overview from "../components/admin/Overview";
import QuotesList from "../components/admin/QuotesList";
import CalendarAgenda from "../components/admin/CalendarAgenda";
import DevisManager from "../components/admin/DevisManager";
import ContentEditor from "../components/admin/ContentEditor";
import Settings from "../components/admin/Settings";
import NotificationsPoll from "../components/admin/NotificationsPoll";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [counts, setCounts] = useState({ quotes_new: 0, appointments_new: 0, total: 0 });
  const [calToken, setCalToken] = useState(null);

  useEffect(() => {
    if (user) api.get("/admin/stats").then(({ data }) => setCalToken(data.calendar_token)).catch(() => {});
  }, [user]);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#050505] text-white/60">Chargement…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const NavTab = ({ value, icon: Icon, label, badge }) => (
    <TabsTrigger
      value={value}
      className="relative flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium text-white/60 data-[state=active]:bg-brand-gradient data-[state=active]:text-white"
      data-testid={`tab-${value}`}
    >
      <Icon size={14} />{label}
      {badge > 0 && (
        <span className="ml-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#e63946] px-1 text-[9px] font-bold text-white">{badge}</span>
      )}
    </TabsTrigger>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <NotificationsPoll onCounts={setCounts} />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Logo" className="h-9 w-auto" />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-head text-sm font-bold uppercase tracking-tight text-white">Admin</span>
              <span className="text-[9px] tracking-[0.25em] text-white/60">EVENT'S MULTITECHNIQUE</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-xs text-white/80 hover:bg-white/5" data-testid="admin-goto-site"><Home size={14} />Voir le site</button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-xs text-white/80 hover:bg-white/5" data-testid="admin-logout"><LogOut size={14} />Déconnexion</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-10">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-white/50">Tableau de bord</p>
          <h1 className="mt-2 font-head text-3xl font-semibold uppercase tracking-tight text-white sm:text-4xl">Bonjour, <span className="text-gradient">Admin</span></h1>
          <p className="mt-2 text-sm font-light text-white/60">Vue d'ensemble de votre activité en temps réel.</p>
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); const u = new URL(window.location.href); u.searchParams.set("tab", v); window.history.replaceState({}, "", u); }}>
          <TabsList className="mb-8 flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            <NavTab value="overview" icon={LayoutDashboard} label="Aperçu" />
            <NavTab value="quotes" icon={MessageSquare} label="Demandes" badge={counts.quotes_new} />
            <NavTab value="appointments" icon={CalendarDays} label="Rendez-vous" badge={counts.appointments_new} />
            <NavTab value="devis" icon={FileText} label="Devis" />
            <NavTab value="content" icon={Palette} label="Contenu" />
            <NavTab value="settings" icon={SettingsIcon} label="Paramètres" />
          </TabsList>
          <TabsContent value="overview" className="mt-0"><Overview /></TabsContent>
          <TabsContent value="quotes" className="mt-0"><QuotesList /></TabsContent>
          <TabsContent value="appointments" className="mt-0"><CalendarAgenda /></TabsContent>
          <TabsContent value="devis" className="mt-0"><DevisManager /></TabsContent>
          <TabsContent value="content" className="mt-0"><ContentEditor /></TabsContent>
          <TabsContent value="settings" className="mt-0"><Settings calendarToken={calToken} onTokenChange={setCalToken} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
